import styled from '@emotion/styled';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { QcsButton } from './basic/QcsButton';
import { QcsTypography } from './basic/QcsTypography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { QcsLoadingButton } from './basic/QcsLoadingButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios, { CancelToken } from 'axios';
import { useNavigation } from '../../utils/urlPaths';

const DetailHeaderContainer = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  marginBottom: '1.5rem',
  maxWidth: theme.breakpoints.values.md,
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    alignItems: 'center',
  },
}));

const LeftBlock = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '1rem',
  minWidth: 0,
});

const BackButtonText = styled.span(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

interface Props {
  title?: string | null;
  titleContent?: ReactNode;
  backText?: string;
  backTo?: string | null;
  showBackTo?: boolean;
  onRefresh?: (cancelToken: CancelToken) => void;
  children?: ReactNode;
}

export const DetailHeader: FC<Props> = ({ title, titleContent, backText, backTo, onRefresh, showBackTo, children }) => {
  const { t } = useTranslation();
  const { navigate } = useNavigation();

  const handleBackClick = () => {
    backTo ? navigate(backTo) : window.history.back();
  };

  const handleRefresh = () => {
    if (!onRefresh) return;
    onRefresh(axios.CancelToken.source().token);
  };

  return (
    <DetailHeaderContainer>
      <LeftBlock>
        {(!!backTo || showBackTo) && (
          <QcsButton variant="outlined" onClick={handleBackClick} sx={{ flexShrink: 0 }}>
            <ArrowBackIcon />
            <BackButtonText>{backText ? t(backText) : t('common.back')}</BackButtonText>
          </QcsButton>
        )}
        {!!title && <QcsTypography variant="h4">{t(title)}</QcsTypography>}
        {titleContent}
      </LeftBlock>
      {onRefresh && (
        <QcsLoadingButton sx={{ marginLeft: 'auto' }} variant="outlined" onClick={handleRefresh}>
          <RefreshIcon />
        </QcsLoadingButton>
      )}
      {children && <div style={{ marginLeft: 'auto' }}>{children}</div>}
    </DetailHeaderContainer>
  );
};
